import { ChevronLeft, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { ApiError } from "@/api/client";

import { centsFromAmount, QuoteUnavailableError, speedLabel } from "./adapter";
import { useCashier } from "./CashierProvider";
import { CoinflowFrame, type CoinflowMessage } from "./CoinflowFrame";
import { CryptoWithdrawPane } from "./CryptoPanes";
import { PaymentMethodBar } from "./payment-methods/PaymentMethodBar";
import type { CashierQuote } from "./types";

const MIN_WITHDRAWAL_CENTS = 10_00;

type WithdrawView = "form" | "iframe" | "success" | "kyc" | "geo";

type ActionError =
  | { kind: "inline"; text: string }
  | { kind: "vendor_outage"; text: string }
  | {
      kind: "insufficient_funds";
      text: string;
      suggestedAmount: string | null;
    };

const fmtUsd = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

export function WithdrawPane({
  onFullscreen,
}: {
  onFullscreen: (fullscreen: boolean) => void;
}) {
  const { adapter, adapterSnapshot, selectedWallet, refresh, closeCashier } =
    useCashier();
  const context = adapterSnapshot?.withdraw ?? null;

  const [view, setView] = useState<WithdrawView>("form");
  const [selectedToken, setSelectedToken] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [quotes, setQuotes] = useState<readonly CashierQuote[]>([]);
  const [selectedSpeed, setSelectedSpeed] = useState<string | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<ActionError | null>(null);
  const [gate, setGate] = useState<{
    detail: string;
    verificationLink: string | null;
  } | null>(null);
  const [bankAuthUrl, setBankAuthUrl] = useState<string | null>(null);
  const [successDetail, setSuccessDetail] = useState<string | null>(null);
  // TiltRips opens Withdraw on Crypto; Cash / Bank is selected from the
  // payment-method bar and then takes over the larger Coinflow surface.
  const [rail, setRail] = useState<"cash" | "crypto">("crypto");
  const cryptoAvailable = Boolean(
    adapterSnapshot?.crypto?.assets.some((asset) => asset.withdrawEnabled),
  );
  const effectiveRail = cryptoAvailable ? rail : "cash";

  const accounts = useMemo(() => context?.accounts ?? [], [context?.accounts]);
  const currentAccount = useMemo(
    () =>
      accounts.find((account) => account.token === selectedToken) ??
      accounts[0] ??
      null,
    [accounts, selectedToken],
  );

  useEffect(() => {
    onFullscreen(effectiveRail === "cash");
    return () => onFullscreen(false);
  }, [effectiveRail, onFullscreen]);

  // Amount or account edits invalidate any standing quotes (see the
  // change handlers — a stale fee must never be confirmed).
  const clearQuotes = () => {
    setQuotes([]);
    setSelectedSpeed(null);
  };

  const limitsLabel = useMemo(() => {
    if (!context) return null;
    const parts: string[] = [];
    if (context.minWithdrawalCents != null) {
      parts.push(`Min ${fmtUsd(context.minWithdrawalCents)}`);
    }
    if (context.maxWithdrawalCents != null) {
      parts.push(`Max ${fmtUsd(context.maxWithdrawalCents)}`);
    }
    return parts.length ? parts.join(" · ") : null;
  }, [context]);

  const classifyError = useCallback(
    (error: unknown): void => {
      if (error instanceof QuoteUnavailableError) {
        setActionError({ kind: "inline", text: error.message });
        return;
      }
      if (error instanceof ApiError) {
        if (error.code === "withdrawals_kyc_required") {
          setGate({
            detail: error.message,
            verificationLink:
              typeof error.details.verification_link === "string"
                ? error.details.verification_link
                : null,
          });
          setView("kyc");
          return;
        }
        if (error.code === "withdrawals_geo_blocked") {
          setGate({ detail: error.message, verificationLink: null });
          setView("geo");
          return;
        }
        if (error.code === "insufficient_funds") {
          const shortfall =
            typeof error.details.shortfall === "string"
              ? Number.parseFloat(error.details.shortfall)
              : NaN;
          const requested = Number.parseFloat(amount);
          const suggested =
            Number.isFinite(shortfall) && Number.isFinite(requested)
              ? Math.max(0, Math.floor((requested - shortfall) * 100) / 100)
              : null;
          setActionError({
            kind: "insufficient_funds",
            text: error.message,
            suggestedAmount:
              suggested && suggested > 0 ? suggested.toFixed(2) : null,
          });
          return;
        }
        if (
          error.code === "psp_upstream_unavailable" ||
          error.code === "psp_not_configured" ||
          error.status === 503
        ) {
          setActionError({ kind: "vendor_outage", text: error.message });
          return;
        }
        setActionError({ kind: "inline", text: error.message });
        return;
      }
      setActionError({
        kind: "inline",
        text: "Something went wrong. Please try again.",
      });
    },
    [amount],
  );

  const startLinkAccount = async () => {
    setActionError(null);
    if (!adapter?.createBankAuthSession) return;
    // Prefer the fresh session URL; fall back to the methods-read URL.
    try {
      const url = await adapter.createBankAuthSession(
        new AbortController().signal,
      );
      setBankAuthUrl(url);
      setView("iframe");
    } catch (error) {
      if (context?.bankAuthUrl) {
        setBankAuthUrl(context.bankAuthUrl);
        setView("iframe");
        return;
      }
      classifyError(error);
    }
  };

  const finishBankAuth = useCallback(() => {
    setBankAuthUrl(null);
    setView("form");
    void refresh();
  }, [refresh]);

  const handleBankAuthMessage = (message: CoinflowMessage) => {
    if (
      message.kind === "accountLinked" ||
      message.kind === "bankAuthCompleted"
    ) {
      finishBankAuth();
    } else if (message.kind === "error") {
      setBankAuthUrl(null);
      setView("form");
      setActionError({ kind: "inline", text: message.message });
    }
  };

  const requestQuotes = async (amountOverride?: string) => {
    const effectiveAmount = amountOverride ?? amount;
    const amountCents = centsFromAmount(effectiveAmount);
    setActionError(null);
    if (!adapter?.getWithdrawQuotes || !currentAccount) return false;
    if (amountCents < MIN_WITHDRAWAL_CENTS) {
      setActionError({
        kind: "inline",
        text: `Minimum withdrawal is ${fmtUsd(MIN_WITHDRAWAL_CENTS)}.`,
      });
      return false;
    }
    setQuoting(true);
    try {
      const next = await adapter.getWithdrawQuotes(
        amountCents,
        currentAccount.token,
        currentAccount.speeds,
        new AbortController().signal,
      );
      setQuotes(next);
      setSelectedSpeed(next[0]?.id ?? null);
      return true;
    } catch (error) {
      classifyError(error);
      return false;
    } finally {
      setQuoting(false);
    }
  };

  const confirmPayout = async (amountOverride?: string) => {
    const effectiveAmount = amountOverride ?? amount;
    setActionError(null);
    if (
      !adapter?.beginWithdraw ||
      !currentAccount ||
      !selectedWallet ||
      !selectedSpeed
    ) {
      return;
    }
    const quote = quotes.find((row) => row.id === selectedSpeed) ?? null;
    setSubmitting(true);
    try {
      const result = await adapter.beginWithdraw(
        {
          flow: "withdraw",
          walletId: selectedWallet.id,
          methodId: currentAccount.token,
          amount: effectiveAmount,
          currency: "USD",
          quote,
        },
        new AbortController().signal,
      );
      setSuccessDetail(
        result.message ??
          "Your withdrawal is being processed. Delivery starts once it is approved.",
      );
      setView("success");
      setAmount("");
      setQuotes([]);
      setSelectedSpeed(null);
      void refresh();
    } catch (error) {
      classifyError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const retryWithSuggested = async (suggested: string) => {
    setAmount(suggested);
    const quoted = await requestQuotes(suggested);
    if (quoted) await confirmPayout(suggested);
  };

  const leaveCash = () => {
    if (view === "iframe") {
      setBankAuthUrl(null);
      setView("form");
      return;
    }
    if (cryptoAvailable) {
      setRail("crypto");
      return;
    }
    closeCashier();
  };

  const cashTakeover = (content: ReactNode, fill = false) => (
    <div className="cashier-takeover">
      <div className="cashier-takeover__bar">
        <button
          aria-label="Back"
          className="cashier-takeover__back"
          onClick={leaveCash}
          type="button"
        >
          <ChevronLeft aria-hidden size={14} />
          <span>Back</span>
        </button>
        <span className="cashier-takeover__title">Cash</span>
        <button
          aria-label="Close"
          className="cashier-takeover__close"
          onClick={closeCashier}
          type="button"
        >
          <X aria-hidden size={17} />
        </button>
      </div>
      <div
        className={`cashier-takeover__body${fill ? " is-fill" : " is-form"}`}
      >
        {content}
      </div>
    </div>
  );

  if (!adapter) {
    return (
      <div className="cashier-state">
        <strong>Cashier unavailable</strong>
        <p>No withdrawal request will be made.</p>
      </div>
    );
  }

  if (!context) {
    return (
      <div className="cashier-state" role="status">
        <span aria-hidden className="cashier-frame__spinner" />
        <strong>Loading withdrawal methods…</strong>
      </div>
    );
  }

  if (view === "iframe" && bankAuthUrl) {
    return cashTakeover(
      <CoinflowFrame
        onMessage={handleBankAuthMessage}
        title="Coinflow Bank Link"
        url={bankAuthUrl}
      />,
      true,
    );
  }

  if (view === "kyc") {
    return cashTakeover(
      <div className="cashier-state">
        <strong>Verify your identity</strong>
        <p>
          {gate?.detail ?? "Please complete identity verification to withdraw."}
        </p>
        {gate?.verificationLink ? (
          <a
            className="cashier-state__retry is-primary"
            href={gate.verificationLink}
            rel="noopener noreferrer"
            target="_blank"
          >
            Complete verification
          </a>
        ) : null}
        <button
          className="cashier-state__retry"
          onClick={() => {
            setGate(null);
            setView("form");
            void refresh();
          }}
          type="button"
        >
          I&rsquo;ve verified — refresh
        </button>
      </div>,
    );
  }

  if (view === "geo") {
    return cashTakeover(
      <div className="cashier-state">
        <strong>Withdrawals unavailable here</strong>
        <p>{gate?.detail ?? "Withdrawals are not available in your region."}</p>
      </div>,
    );
  }

  if (view === "success") {
    return cashTakeover(
      <div className="cashier-state" role="status">
        <strong>Withdrawal Submitted</strong>
        <p>{successDetail}</p>
        <button
          className="cashier-state__retry"
          onClick={() => setView("form")}
          type="button"
        >
          Done
        </button>
      </div>,
    );
  }

  if (effectiveRail === "crypto") {
    return (
      <div className="cashier-pane cashier-withdraw">
        <div className="cashier-section-label">Payment Method</div>
        <PaymentMethodBar
          ariaLabel="Withdrawal method"
          current={{ id: "crypto", label: "Crypto" }}
          onSelect={(methodId) => {
            if (methodId === "fiat") setRail("cash");
          }}
          options={[
            { id: "crypto", label: "Crypto" },
            { id: "fiat", label: "Cash / Bank" },
          ]}
        />
        <CryptoWithdrawPane />
      </div>
    );
  }

  return cashTakeover(
    <div className="cashier-withdraw-takeover__form">
      <div className="cashier-section-label">Withdrawal Method</div>
      <div className="cashier-withdraw__methods">
        {accounts.map((account) => (
          <button
            data-selected={account.token === currentAccount?.token || undefined}
            key={account.token}
            onClick={() => {
              setSelectedToken(account.token);
              setActionError(null);
              clearQuotes();
            }}
            type="button"
          >
            <span>{account.label}</span>
          </button>
        ))}
        {accounts.length === 0 ? (
          <p className="cashier-withdraw__empty">
            No withdrawal method linked yet.
          </p>
        ) : null}
      </div>
      <button
        className="cashier-withdraw__link-new"
        onClick={() => void startLinkAccount()}
        type="button"
      >
        + Link New Withdraw Method
      </button>

      <div className="cashier-withdraw__amount-head">
        <div className="cashier-section-label">Amount (USD)</div>
        {limitsLabel ? <span>{limitsLabel}</span> : null}
      </div>
      <input
        aria-label="Withdrawal amount"
        autoComplete="off"
        className="cashier-withdraw__amount"
        inputMode="decimal"
        min={MIN_WITHDRAWAL_CENTS / 100}
        onChange={(event) => {
          setAmount(event.target.value);
          clearQuotes();
        }}
        placeholder="Enter amount"
        step="0.01"
        type="number"
        value={amount}
      />

      <button
        className="cashier-cta is-quote"
        disabled={quoting || !currentAccount}
        onClick={() => void requestQuotes()}
        type="button"
      >
        {quoting ? "Fetching quote…" : "Get Quote"}
      </button>

      {quotes.length > 0 ? (
        <>
          <div className="cashier-section-label">
            {quotes.length > 1 ? "Delivery Speed" : "Delivery"}
          </div>
          <div className="cashier-withdraw__quotes">
            {quotes.map((quote) => (
              <button
                data-selected={quote.id === selectedSpeed || undefined}
                key={quote.id}
                onClick={() => setSelectedSpeed(quote.id)}
                type="button"
              >
                <span className="cashier-withdraw__quote-head">
                  <strong>{speedLabel(quote.id)}</strong>
                  <span>{quote.total}</span>
                </span>
                <span className="cashier-withdraw__quote-detail">
                  Fee {quote.fee}
                  {quote.disclosure
                    ? ` · ${quote.disclosure} after approval`
                    : ""}
                </span>
              </button>
            ))}
          </div>
          <p className="cashier-withdraw__review-note">
            Withdrawals are reviewed before being sent — this can take a few
            hours during busy periods. Delivery time starts once your withdrawal
            is approved.
          </p>
          <button
            className="cashier-cta is-confirm"
            disabled={submitting}
            onClick={() => void confirmPayout()}
            type="button"
          >
            {submitting ? "Submitting..." : "Confirm Withdrawal"}
          </button>
        </>
      ) : null}

      {actionError?.kind === "vendor_outage" ? (
        <div className="cashier-banner is-amber" role="alert">
          <strong>Withdrawals are temporarily unavailable</strong>
          <p>{actionError.text}</p>
          <small>
            Your funds are safe — this is on our payment partner&rsquo;s side.
            Please try again in a few minutes.
          </small>
        </div>
      ) : actionError?.kind === "insufficient_funds" ? (
        <div className="cashier-banner is-red" role="alert">
          <strong>Amount exceeds your balance</strong>
          <p>{actionError.text}</p>
          {actionError.suggestedAmount ? (
            <button
              disabled={submitting || quoting}
              onClick={() =>
                void retryWithSuggested(actionError.suggestedAmount ?? "")
              }
              type="button"
            >
              {submitting || quoting
                ? "Submitting..."
                : `Withdraw $${actionError.suggestedAmount} instead`}
            </button>
          ) : null}
          <small>
            Nothing was deducted from your balance. Withdrawal fees are added on
            top of the amount you enter.
          </small>
        </div>
      ) : actionError ? (
        <p className="cashier-method-error" role="alert">
          {actionError.text}
        </p>
      ) : null}
    </div>,
  );
}
